import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Закреплённое представление на своём маршруте.
//
// Описание берётся из базы, а исполняется правами того, кто открыл: поделённое
// представление показывает только те записи, которые человеку и так доступны.
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Button, Popconfirm, Space, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { ViewRenderer } from '../components/ViewRenderer.js';
import { SaveViewModal } from '../components/SaveViewModal.js';
import {
  useDeleteSavedView,
  useSavedViewData,
  useSavedViews,
  useUpdateSavedView,
} from '../hooks/useSavedViews.js';

const { Paragraph } = Typography;

/** Quiet loading placeholder — the shared list chrome, not a bare Spin. */
function QuietPageLoading(): ReactElement {
  return (
    <div className="p-6">
      <div style={LIST_SURFACE_STYLE}>
        <QuietListSkeleton rows={3} />
      </div>
    </div>
  );
}

export default function SavedViewPage(): ReactElement {
  useI18nSubscription();
  const { viewId } = useParams();
  const navigate = useNavigate();
  const views = useSavedViews();
  const data = useSavedViewData(viewId);
  const update = useUpdateSavedView();
  const remove = useDeleteSavedView();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const view = useMemo(() => views.data?.find((item) => item.id === viewId), [views.data, viewId]);

  if (views.isLoading) {
    return <QuietPageLoading />;
  }
  if (view === undefined) {
    return (
      <ErrorState
        title={i18n.t('crm.features.features.views.pages.savedviewpage.представление_не_найдено')}
        fallbackDescription={i18n.t(
          'crm.features.features.views.pages.savedviewpage.его_удалили_или_доступ_к_нему_закрыли',
        )}
        variant="page"
      />
    );
  }

  // Sharing markers sit under the title as token chips (was `<Tag color="blue">`).
  const chips: ReactNode[] = [];
  if (!view.own) {
    // Чужое представление правит только его владелец — поэтому важно
    // сразу видеть, чьё оно.
    chips.push(
      <EntityStatusChip key="owner">
        {i18n.t('crm.features.dynamic.features.views.pages.savedviewpage.83', {
          p0: view.ownerName,
        })}
      </EntityStatusChip>,
    );
  }
  if (view.sharedToAll) {
    chips.push(
      <EntityStatusChip key="all" tone="processing">
        {i18n.t('crm.features.features.views.pages.savedviewpage.видно_всем')}
      </EntityStatusChip>,
    );
  }
  if (view.sharedWithMemberIds.length > 0) {
    chips.push(
      <EntityStatusChip key="members" tone="processing">
        {i18n.t('crm.features.dynamic.features.views.pages.savedviewpage.84', {
          p0: view.sharedWithMemberIds.length,
        })}
      </EntityStatusChip>,
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title={view.title}
        {...(chips.length > 0 ? { subtitle: <Space size="small">{chips}</Space> } : {})}
        {...(view.own
          ? {
              actions: (
                <Space>
                  <Button onClick={() => setSettingsOpen(true)}>
                    {i18n.t('crm.features.features.views.pages.savedviewpage.настроить')}
                  </Button>
                  <Popconfirm
                    title={i18n.t(
                      'crm.features.features.views.pages.savedviewpage.убрать_представление',
                    )}
                    okText={i18n.t('crm.features.features.views.pages.savedviewpage.убрать')}
                    cancelText={i18n.t('crm.features.features.views.pages.savedviewpage.отмена')}
                    onConfirm={async () => {
                      await remove.mutateAsync(view.id);
                      navigate('/views');
                    }}
                  >
                    <Button danger>
                      {i18n.t('crm.features.features.views.pages.savedviewpage.убрать_2')}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            }
          : {})}
      />

      {data.isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.views.pages.savedviewpage.не_удалось_собрать_представление',
          )}
          error={data.error}
          variant="inline"
          onRetry={() => void data.refetch()}
        />
      ) : null}
      {data.isLoading ? (
        <div style={LIST_SURFACE_STYLE}>
          <QuietListSkeleton rows={3} />
        </div>
      ) : null}
      {data.data ? <ViewRenderer result={data.data} showTitle={false} /> : null}

      {data.data?.blocks.length === 0 ? (
        <Paragraph type="secondary">
          {i18n.t(
            'crm.features.features.views.pages.savedviewpage.описание_не_содержит_ни_одного_блока',
          )}
        </Paragraph>
      ) : null}

      <SaveViewModal
        open={settingsOpen}
        spec={view.spec}
        existing={view}
        saving={update.isPending}
        error={update.error}
        onCancel={() => setSettingsOpen(false)}
        onSubmit={async (payload) => {
          await update.mutateAsync({ id: view.id, payload });
          setSettingsOpen(false);
        }}
      />
    </div>
  );
}
